<script setup>
// @ts-check

import BaseErrors from '@/components/base/BaseErrors.vue'
import CheckIcon from '@/components/icons/CheckIcon.vue'

const props = defineProps({
  /** Only use this prop if you only want accessible label */
  label: {
    type: String,
    default: null
  },
  modelValue: {
    type: Boolean
  },
  disabled: {
    type: Boolean
  },
  errors: {
    /** @type {import('vue').PropType<string[]|null>} */
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

defineOptions({
  inheritAttrs: false
})

const toggleValue = () => {
  emit('change', !props.modelValue)
  emit('update:modelValue', !props.modelValue)
}
</script>

<template>
  <component
    v-bind="$attrs"
    :is="$slots.default ? 'label' : 'div'"
    class="flex cursor-pointer flex-row items-center"
    :class="{
      'pointer-events-none': disabled
    }"
    @click.stop="$slots.default ? null : toggleValue()"
  >
    <input
      class="hidden"
      type="checkbox"
      :checked="modelValue"
      :value="modelValue"
      :disabled
      :aria-label="label"
      tabindex="-1"
      @input="toggleValue"
    />
    <CheckIcon
      class="outline-hidden"
      :tabindex="disabled ? -1 : 0"
      :status="modelValue"
      :disabled
      @keypress.enter="toggleValue"
    />
    <div
      v-if="$slots.default"
      style="user-select: none"
      class="ml-3"
      :class="{ 'text-gray-400': disabled }"
    >
      <slot></slot>
      <p v-if="$slots.info" class="text-secondary-400">
        <slot name="info"></slot>
      </p>
    </div>
  </component>
  <BaseErrors class="mt-2" :errors />
</template>

<style lang="scss" scoped>
@reference '../../styles/main.css';

:deep(.error-message) {
  @apply text-xs;
}
</style>
